import { useState, useCallback } from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  Select,
  Button,
  Group,
  Stack,
  SegmentedControl,
  Text,
  Card,
  Checkbox,
  Progress,
  Alert,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { Dropzone } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { Upload, Link as LinkIcon, File, AlertCircle, Info } from 'react-feather';
import type { ResourceCategory } from '../../../types';
import { resourcesApi } from '../../../services/api/resources';

// Allowed file types
const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'video/mp4',
  'video/quicktime',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

interface AddEvergreenResourceModalProps {
  opened: boolean;
  onClose: () => void;
  categories: ResourceCategory[];
  onCreated: () => void;
}

export function AddEvergreenResourceModal({
  opened,
  onClose,
  categories,
  onCreated,
}: AddEvergreenResourceModalProps) {
  const [resourceType, setResourceType] = useState<'file' | 'link'>('link');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const form = useForm({
    initialValues: {
      title: '',
      description: '',
      url: '',
      category_id: '',
      access_coaches: true,
      access_participants: false,
      access_partners: false,
    },
    validate: {
      title: (value) => (value.trim() ? null : 'Title is required'),
      url: (value) =>
        resourceType === 'link'
          ? value.trim()
            ? null
            : 'URL is required'
          : null,
    },
  });

  const handleClose = () => {
    form.reset();
    setSelectedFile(null);
    setResourceType('link');
    setUploadProgress(0);
    onClose();
  };

  const handleFileDrop = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0];
      setSelectedFile(file);
      // Auto-fill title from filename if empty
      if (!form.values.title) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        form.setFieldValue('title', nameWithoutExt);
      }
    }
  };

  const uploadFile = useCallback(
    async (file: File): Promise<{ key: string }> => {
      // Get presigned URL - no cohortId for evergreen resources
      const uploadInfo = await resourcesApi.getUploadUrl(
        file.name,
        file.type,
      );

      // Upload to S3
      const xhr = new XMLHttpRequest();

      return new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(percent);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ key: uploadInfo.key });
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'));
        });

        xhr.open('PUT', uploadInfo.upload_url);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
    },
    [],
  );

  const handleSubmit = async (values: typeof form.values) => {
    try {
      setUploading(true);

      if (resourceType === 'link') {
        // Create evergreen link resource (omit cohort_id for evergreen)
        await resourcesApi.createLinkResource({
          cohort_id: undefined as unknown as string, // undefined = evergreen (omitted from request)
          title: values.title,
          description: values.description || undefined,
          url: values.url,
          category_id: values.category_id || undefined,
          access_coaches: values.access_coaches,
          access_participants: values.access_participants,
          access_partners: values.access_partners,
          is_evergreen: true,
        });
      } else {
        // Upload file and create evergreen resource
        if (!selectedFile) {
          notifications.show({
            title: 'Error',
            message: 'Please select a file to upload',
            color: 'red',
          });
          setUploading(false);
          return;
        }

        const { key } = await uploadFile(selectedFile);

        await resourcesApi.createFileResource({
          cohort_id: undefined as unknown as string, // undefined = evergreen (omitted from request)
          title: values.title,
          description: values.description || undefined,
          file_path: key,
          file_size: selectedFile.size,
          mime_type: selectedFile.type,
          original_filename: selectedFile.name,
          category_id: values.category_id || undefined,
          access_coaches: values.access_coaches,
          access_participants: values.access_participants,
          access_partners: values.access_partners,
          is_evergreen: true,
        });
      }

      onCreated();
      handleClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to add resource',
        color: 'red',
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const categoryOptions = categories.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Add Evergreen Resource"
      size="lg"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Info banner */}
          <Alert icon={<Info size={16} />} color="teal">
            <Text size="sm">
              <strong>Evergreen resources</strong> are available to all cohorts by default.
              Cohort managers can choose to hide specific evergreen resources from their cohorts.
            </Text>
          </Alert>

          {/* Resource type toggle */}
          <SegmentedControl
            value={resourceType}
            onChange={(value) => setResourceType(value as 'file' | 'link')}
            data={[
              {
                value: 'link',
                label: (
                  <Group gap="xs">
                    <LinkIcon size={14} />
                    <span>Link / URL</span>
                  </Group>
                ),
              },
              {
                value: 'file',
                label: (
                  <Group gap="xs">
                    <Upload size={14} />
                    <span>Upload File</span>
                  </Group>
                ),
              },
            ]}
            fullWidth
          />

          {/* File upload dropzone */}
          {resourceType === 'file' && (
            <Dropzone
              onDrop={handleFileDrop}
              maxSize={MAX_FILE_SIZE}
              accept={ALLOWED_MIME_TYPES}
              multiple={false}
              disabled={uploading}
            >
              <Group justify="center" gap="xl" mih={120} style={{ pointerEvents: 'none' }}>
                {selectedFile ? (
                  <Stack align="center" gap="xs">
                    <File size={40} />
                    <Text size="sm" fw={500}>
                      {selectedFile.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                    </Text>
                  </Stack>
                ) : (
                  <Stack align="center" gap="xs">
                    <Upload size={40} />
                    <Text size="sm">
                      Drag a file here or click to browse
                    </Text>
                    <Text size="xs" c="dimmed">
                      Max file size: 50MB
                    </Text>
                  </Stack>
                )}
              </Group>
            </Dropzone>
          )}

          {/* Upload progress */}
          {uploading && uploadProgress > 0 && (
            <Progress value={uploadProgress} size="sm" />
          )}

          {/* URL input for links */}
          {resourceType === 'link' && (
            <TextInput
              label="URL"
              placeholder="https://example.com/resource"
              required
              {...form.getInputProps('url')}
            />
          )}

          {/* Common fields */}
          <TextInput
            label="Title"
            placeholder="Resource title"
            required
            {...form.getInputProps('title')}
          />

          <Textarea
            label="Description"
            placeholder="Optional description"
            autosize
            minRows={2}
            {...form.getInputProps('description')}
          />

          <Select
            label="Category"
            placeholder="Select category"
            data={categoryOptions}
            clearable
            {...form.getInputProps('category_id')}
          />

          {/* Access permissions */}
          <Card withBorder p="sm">
            <Text size="sm" fw={500} mb="sm">
              Access Permissions
            </Text>
            <Text size="xs" c="dimmed" mb="md">
              Select which user groups can view this resource across all cohorts
            </Text>
            <Group>
              <Checkbox
                label="Coaches"
                {...form.getInputProps('access_coaches', { type: 'checkbox' })}
              />
              <Checkbox
                label="Participants"
                {...form.getInputProps('access_participants', { type: 'checkbox' })}
              />
              <Checkbox
                label="Partners"
                {...form.getInputProps('access_partners', { type: 'checkbox' })}
              />
            </Group>
          </Card>

          {/* At least one access must be selected */}
          {!form.values.access_coaches &&
            !form.values.access_participants &&
            !form.values.access_partners && (
              <Alert icon={<AlertCircle size={16} />} color="yellow">
                At least one access group should be selected
              </Alert>
            )}

          {/* Actions */}
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={handleClose} disabled={uploading}>
              Cancel
            </Button>
            <Button type="submit" loading={uploading}>
              {resourceType === 'file' ? 'Upload & Add' : 'Add Resource'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
